import { useMemo, useState } from 'react'
import { Filters } from '@haus-storefront-react/filters'
import { FacetFiltersAccordion } from './components/FacetFiltersAccordion'
import { FacetFiltersDropdown } from './components/FacetFiltersDropdown'
import { AriaLiveRegion, WidgetReadyBoundary } from '@components'
import styles from './Filters.module.scss'
import { type EnabledFilter, type FacetValueFilterType } from '@haus-storefront-react/shared-types'
import { type FiltersAttributes } from './_propsFn'
import classNames from 'classnames'
import { DefaultFilterUrlStrategy } from '@haus-storefront-react/strategies'
import './FiltersSkeleton.css'
import { useWidgetReadyAfterInitialLoad } from '@components'

const DEFAULT_OPEN_ACCORDIONS = 3
const FILTERS_STRATEGIES = { filtersStoreStrategy: new DefaultFilterUrlStrategy() }

type FacetEnabledFilter = EnabledFilter<FacetValueFilterType>

interface FiltersResolvedContentProps {
  displayType: FiltersAttributes<FacetEnabledFilter>['displayType']
  facetFilters: FacetEnabledFilter[]
  filterAnnouncement: string
  filtersContext: unknown
  onFilterAnnounce: (message: string) => void
  toggleFacetValue: (facetCode: string, valueId: string) => void
}

const FiltersResolvedContent = ({
  displayType = 'dropdown',
  facetFilters,
  filterAnnouncement,
  onFilterAnnounce,
  toggleFacetValue,
}: FiltersResolvedContentProps) => {
  const [loadedFacets, setLoadedFacets] = useState<Set<string>>(new Set())

  const markFacetResolved = (facetCode: string): void => {
    if (loadedFacets.has(facetCode)) return
    setLoadedFacets((prev) => new Set([...prev, facetCode]))
  }

  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent: loadedFacets.size >= facetFilters.length,
    hasVisibleContent: true,
    isLoading: false,
  })
  let visibleAccordionIndex = 0

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <AriaLiveRegion message={filterAnnouncement} />
      <div className={classNames(styles.filtersContent, styles[`filtersContent--${displayType}`])}>
        {displayType === 'dropdown' &&
          facetFilters.map((filter) => (
            <Filters.Group key={filter.facetCode} facetCode={filter.facetCode}>
              {() => (
                <FacetFiltersDropdown
                  facetFilter={filter}
                  toggleFacetValue={toggleFacetValue}
                  onFilterAnnounce={onFilterAnnounce}
                  onResolved={() => markFacetResolved(filter.facetCode)}
                />
              )}
            </Filters.Group>
          ))}
        {displayType === 'accordion' &&
          facetFilters.map((filter) => (
            <Filters.Group key={filter.facetCode} facetCode={filter.facetCode}>
              {(facet) => {
                const currentVisibleIndex = visibleAccordionIndex
                visibleAccordionIndex += 1

                return (
                  <FacetFiltersAccordion
                    facetFilter={filter}
                    facet={facet}
                    toggleFacetValue={toggleFacetValue}
                    onFilterAnnounce={onFilterAnnounce}
                    defaultOpen={currentVisibleIndex < DEFAULT_OPEN_ACCORDIONS}
                    index={currentVisibleIndex}
                    onResolved={() => markFacetResolved(filter.facetCode)}
                  />
                )
              }}
            </Filters.Group>
          ))}
      </div>
    </WidgetReadyBoundary>
  )
}

export default function FiltersComponent({
  productListIdentifier,
  enabledFilters,
  displayType = 'dropdown',
}: FiltersAttributes<EnabledFilter<FacetValueFilterType>>) {
  const [filterAnnouncement, setFilterAnnouncement] = useState('')
  const facetFilters = useMemo(
    () =>
      (enabledFilters ?? []).filter(
        (filter): filter is FacetEnabledFilter =>
          filter.type === 'facet' || filter.type === undefined,
      ),
    [enabledFilters],
  )

  return (
    <Filters.Root
      enabledFilters={enabledFilters}
      productListIdentifier={productListIdentifier}
      strategies={FILTERS_STRATEGIES}
    >
      {(ctx) => {
        if (ctx.facetValues === undefined) {
          return null
        }

        return (
          <FiltersResolvedContent
            displayType={displayType}
            facetFilters={facetFilters}
            filterAnnouncement={filterAnnouncement}
            filtersContext={ctx}
            onFilterAnnounce={setFilterAnnouncement}
            toggleFacetValue={ctx.toggleFacetValue}
          />
        )
      }}
    </Filters.Root>
  )
}
